import { latLng, type LatLngTuple } from 'leaflet';
import { getDay, parseISO } from 'date-fns';
import type { MoTravelDay, MoTravelRoadLeg, MoTravelStop } from '../types/moTravel.types';
import { getStartKey, getStopKey } from './stopKey';
import { buildWalkingCurve } from './walkingCurve';

const WEEKDAYS = [
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
] as const;
type Weekday = (typeof WEEKDAYS)[number];

const FIRST_WORK_WEEKDAY: Weekday = 'SATURDAY';

const WEEKDAY_ROUTE_COLORS: Record<Weekday, string> = {
  SATURDAY: '#2563eb',
  SUNDAY: '#db2777',
  MONDAY: '#d97706',
  TUESDAY: '#7c3aed',
  WEDNESDAY: '#0891b2',
  THURSDAY: '#854d0e',
  FRIDAY: '#475569',
};

const START_WAYPOINT_LABEL = 'S';
const START_TITLE_FALLBACK = 'Starting location';
const MIN_OFF_ROAD_WALK_METERS = 15;

export type WaypointKind = 'start' | 'stop' | 'end';

export interface RouteWaypoint {
  key: string;
  position: LatLngTuple;
  kind: WaypointKind;
  label: string;
  title: string;
  time: string | null;
  stop: MoTravelStop | null;
}

export interface LegRoadSpan {
  startIndex: number;
  endIndex: number;
}

export interface DayRoute {
  day: MoTravelDay;
  color: string;
  waypoints: RouteWaypoint[];
  legPaths: LatLngTuple[][];
  legRoadSpans: LegRoadSpan[];
  path: LatLngTuple[];
}

interface RouteLeg {
  path: LatLngTuple[];
  roadSpan: LegRoadSpan;
}

interface WaypointMatch {
  route: DayRoute;
  index: number;
  waypoint: RouteWaypoint;
}

type LocatedStop = MoTravelStop & { lat: number; lon: number };

export const isLocated = (stop: MoTravelStop): stop is LocatedStop =>
  stop.lat !== null && stop.lon !== null;

export const hasGpsPoints = (days: MoTravelDay[]): boolean =>
  days.some((day) => day.start !== null || day.stops.some(isLocated));

export const isStartWaypoint = (waypoint: { kind: string }): boolean => waypoint.kind === 'start';

export const isEndWaypoint = (waypoint: { kind: string }): boolean => waypoint.kind === 'end';

export const getStartTitle = (day: MoTravelDay): string =>
  day.start?.name?.trim() || START_TITLE_FALLBACK;

export const getStopTitle = (stop: MoTravelStop): string =>
  stop.name ?? stop.instituteName ?? stop.reference;

export const getStopLabel = (stop: MoTravelStop): string => String(stop.seq);

export function findWaypointByKey(routes: DayRoute[], key: string): WaypointMatch | null {
  for (const route of routes) {
    const index = route.waypoints.findIndex((waypoint) => waypoint.key === key);
    const waypoint = route.waypoints[index];
    if (waypoint) return { route, index, waypoint };
  }
  return null;
}

export function getDayRouteColor(date: string): string {
  const weekday = WEEKDAYS[getDay(parseISO(date))] ?? FIRST_WORK_WEEKDAY;
  return WEEKDAY_ROUTE_COLORS[weekday];
}

function toStartWaypoint(day: MoTravelDay): RouteWaypoint | null {
  if (!day.start) return null;
  return {
    key: getStartKey(day),
    position: [day.start.lat, day.start.lon],
    kind: 'start',
    label: START_WAYPOINT_LABEL,
    title: getStartTitle(day),
    time: day.startTime,
    stop: null,
  };
}

function toStopWaypoints(day: MoTravelDay): RouteWaypoint[] {
  const locatedStops = day.stops.filter(isLocated);
  const lastStopIndex = locatedStops.length - 1;
  const hasSeveralStops = locatedStops.length > 1;

  return locatedStops.map((stop, stopIndex) => ({
    key: getStopKey(day, stop),
    position: [stop.lat, stop.lon],
    kind: hasSeveralStops && stopIndex === lastStopIndex ? 'end' : 'stop',
    label: getStopLabel(stop),
    title: getStopTitle(stop),
    time: stop.submittedAt ?? stop.timeTo,
    stop,
  }));
}

function buildOffRoadWalk(
  place: LatLngTuple,
  nearestRoadPoint: LatLngTuple | undefined,
): LatLngTuple[] | null {
  if (!nearestRoadPoint) return null;
  if (latLng(place).distanceTo(nearestRoadPoint) < MIN_OFF_ROAD_WALK_METERS) return null;
  return buildWalkingCurve(place, nearestRoadPoint);
}

function toRouteLeg(
  legStart: RouteWaypoint,
  legEnd: RouteWaypoint,
  roadPath: LatLngTuple[],
): RouteLeg {
  const walkToRoad = buildOffRoadWalk(legStart.position, roadPath.at(0));
  const walkFromRoad = buildOffRoadWalk(legEnd.position, roadPath.at(-1))?.toReversed() ?? null;
  const pathBeforeRoad = walkToRoad?.slice(0, -1) ?? [legStart.position];
  const pathAfterRoad = walkFromRoad?.slice(1) ?? [legEnd.position];
  const path = [...pathBeforeRoad, ...roadPath, ...pathAfterRoad];
  const lastIndex = path.length - 1;
  return {
    path,
    roadSpan: {
      startIndex: walkToRoad ? pathBeforeRoad.length : 0,
      endIndex: walkFromRoad ? lastIndex - pathAfterRoad.length : lastIndex,
    },
  };
}

function buildRouteLegs(
  waypoints: RouteWaypoint[],
  roadLegs: MoTravelRoadLeg[] | null,
): RouteLeg[] {
  const matchingRoadLegs = roadLegs?.length === waypoints.length - 1 ? roadLegs : null;

  return waypoints.slice(1).flatMap((legEnd, legIndex) => {
    const legStart = waypoints[legIndex];
    if (!legStart) return [];
    return [toRouteLeg(legStart, legEnd, matchingRoadLegs?.[legIndex]?.path ?? [])];
  });
}

function joinLegPaths(waypoints: RouteWaypoint[], legPaths: LatLngTuple[][]): LatLngTuple[] {
  const [firstWaypoint] = waypoints;
  if (!firstWaypoint) return [];

  const path: LatLngTuple[] = [firstWaypoint.position];
  for (const legPath of legPaths) {
    path.push(...legPath.slice(1));
  }
  return path;
}

function toDayRoute(day: MoTravelDay): DayRoute {
  const startWaypoint = toStartWaypoint(day);
  const stopWaypoints = toStopWaypoints(day);
  const waypoints = startWaypoint ? [startWaypoint, ...stopWaypoints] : stopWaypoints;
  const legs = buildRouteLegs(waypoints, day.roadLegs);
  const legPaths = legs.map((leg) => leg.path);
  return {
    day,
    color: getDayRouteColor(day.date),
    waypoints,
    legPaths,
    legRoadSpans: legs.map((leg) => leg.roadSpan),
    path: joinLegPaths(waypoints, legPaths),
  };
}

export function buildDayRoutes(days: MoTravelDay[]): DayRoute[] {
  return days.map(toDayRoute);
}

export const sortLatestDayFirst = (routes: DayRoute[]): DayRoute[] =>
  routes.toSorted((left, right) => right.day.date.localeCompare(left.day.date));
